export interface AudioAnalysis {
  duration: number;
  bpm?: number | undefined;
  bpmConfidence?: number | undefined;
  musicalKey?: string | undefined;
  keyConfidence?: number | undefined;
}

const clamp = (value: number, min = 0, max = 1) => Math.min(max, Math.max(min, value));

function monoAt(channels: Float32Array[], index: number) {
  let sample = 0;
  for (const channel of channels) sample += channel[index] ?? 0;
  return sample / Math.max(1, channels.length);
}

export function estimateBpm(channels: Float32Array[], sampleRate: number): { bpm?: number; confidence: number } {
  const length = channels[0]?.length ?? 0;
  if (length < sampleRate * 8) return { confidence: 0 };

  const duration = length / sampleRate;
  const start = Math.floor((duration > 55 ? 12 : 0) * sampleRate);
  const end = Math.min(length, start + Math.floor(105 * sampleRate));
  const envelopeRate = 100;
  const blockSize = Math.max(1, Math.floor(sampleRate / envelopeRate));
  const energies: number[] = [];

  for (let offset = start; offset + blockSize < end; offset += blockSize) {
    let energy = 0;
    for (let index = offset; index < offset + blockSize; index += 1) {
      const sample = monoAt(channels, index);
      energy += sample * sample;
    }
    energies.push(Math.sqrt(energy / blockSize));
  }

  const onsets = new Float32Array(energies.length);
  let average = energies[0] ?? 0;
  for (let index = 1; index < energies.length; index += 1) {
    average = average * 0.96 + (energies[index] ?? 0) * 0.04;
    onsets[index] = Math.max(0, (energies[index] ?? 0) - average - (energies[index - 1] ?? 0) * 0.12);
  }

  const correlation = (lag: number) => {
    let product = 0;
    let leftEnergy = 0;
    let rightEnergy = 0;
    for (let index = lag; index < onsets.length; index += 1) {
      const left = onsets[index] ?? 0;
      const right = onsets[index - lag] ?? 0;
      product += left * right;
      leftEnergy += left * left;
      rightEnergy += right * right;
    }
    return product / Math.sqrt(Math.max(1e-12, leftEnergy * rightEnergy));
  };

  let bestBpm = 0;
  let bestScore = 0;
  let scoreTotal = 0;
  const minimumLag = Math.round(envelopeRate * 60 / 190);
  const maximumLag = Math.round(envelopeRate * 60 / 70);
  for (let lag = minimumLag; lag <= maximumLag; lag += 1) {
    const bpm = Math.round(envelopeRate * 60 / lag);
    const score = correlation(lag) + correlation(lag * 2) * 0.42 + correlation(Math.max(1, Math.round(lag / 2))) * 0.18;
    scoreTotal += score;
    if (score > bestScore) { bestScore = score; bestBpm = bpm; }
  }

  const baseline = scoreTotal / (maximumLag - minimumLag + 1);
  const confidence = clamp((bestScore - baseline) / 0.7);
  return bestBpm && confidence >= 0.08 ? { bpm: bestBpm, confidence } : { confidence };
}

const MAJOR_PROFILE = [6.35, 2.23, 3.48, 2.33, 4.38, 4.09, 2.52, 5.19, 2.39, 3.66, 2.29, 2.88];
const MINOR_PROFILE = [6.33, 2.68, 3.52, 5.38, 2.60, 3.53, 2.54, 4.75, 3.98, 2.69, 3.34, 3.17];
const KEY_NAMES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];

function goertzel(samples: Float32Array, sampleRate: number, frequency: number) {
  const omega = 2 * Math.PI * frequency / sampleRate;
  const coefficient = 2 * Math.cos(omega);
  let previous = 0;
  let previous2 = 0;
  for (const sample of samples) {
    const current = sample + coefficient * previous - previous2;
    previous2 = previous;
    previous = current;
  }
  return Math.max(0, previous2 * previous2 + previous * previous - coefficient * previous * previous2);
}

export function estimateKey(channels: Float32Array[], sampleRate: number): { musicalKey?: string; confidence: number } {
  const length = channels[0]?.length ?? 0;
  const windowSize = 4096;
  if (length < windowSize * 4) return { confidence: 0 };

  const chroma = new Float64Array(12);
  const windowCount = Math.min(24, Math.max(8, Math.floor(length / sampleRate / 3)));
  let signalEnergy = 0;
  for (let windowIndex = 0; windowIndex < windowCount; windowIndex += 1) {
    const center = Math.floor((windowIndex + 0.5) * length / windowCount);
    const start = Math.max(0, Math.min(length - windowSize, center - Math.floor(windowSize / 2)));
    const samples = new Float32Array(windowSize);
    for (let index = 0; index < windowSize; index += 1) {
      const hann = 0.5 - 0.5 * Math.cos(2 * Math.PI * index / (windowSize - 1));
      const value = monoAt(channels, start + index) * hann;
      samples[index] = value;
      signalEnergy += value * value;
    }
    for (let midi = 36; midi <= 83; midi += 1) {
      const frequency = 440 * 2 ** ((midi - 69) / 12);
      const pitchClass = midi % 12;
      chroma[pitchClass] = (chroma[pitchClass] ?? 0) + Math.sqrt(goertzel(samples, sampleRate, frequency)) / Math.sqrt(frequency);
    }
  }
  if (signalEnergy < 1e-4) return { confidence: 0 };

  const scores: Array<{ key: string; score: number }> = [];
  for (let root = 0; root < 12; root += 1) {
    for (const [profile, suffix] of [[MAJOR_PROFILE, ""], [MINOR_PROFILE, "m"]] as const) {
      let score = 0;
      let magnitude = 0;
      let profileMagnitude = 0;
      for (let degree = 0; degree < 12; degree += 1) {
        const value = chroma[(degree + root) % 12] ?? 0;
        const weight = profile[degree] ?? 0;
        score += value * weight;
        magnitude += value * value;
        profileMagnitude += weight * weight;
      }
      scores.push({ key: `${KEY_NAMES[root] ?? "C"}${suffix}`, score: score / Math.sqrt(Math.max(1e-12, magnitude * profileMagnitude)) });
    }
  }
  scores.sort((left, right) => right.score - left.score);
  const best = scores[0];
  const second = scores[1];
  if (!best || !second) return { confidence: 0 };
  const confidence = clamp((best.score - second.score) / Math.max(0.01, best.score) * 5);
  return confidence >= 0.025 ? { musicalKey: best.key, confidence } : { confidence };
}

export async function analyzeAudioBlob(blob: Blob): Promise<AudioAnalysis> {
  const context = new AudioContext();
  try {
    const buffer = await context.decodeAudioData(await blob.arrayBuffer());
    const channels = Array.from({ length: buffer.numberOfChannels }, (_, index) => buffer.getChannelData(index));
    const bpm = estimateBpm(channels, buffer.sampleRate);
    const key = estimateKey(channels, buffer.sampleRate);
    return {
      duration: buffer.duration,
      ...(bpm.bpm ? { bpm: bpm.bpm, bpmConfidence: bpm.confidence } : {}),
      ...(key.musicalKey ? { musicalKey: key.musicalKey, keyConfidence: key.confidence } : {}),
    };
  } finally {
    await context.close();
  }
}
